import React from "react";
import {
    Box,
    Typography,
    IconButton,
    Tooltip
} from "@mui/material";
import {
    Edit as EditIcon,
    Delete as DeleteIcon
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { theme } from "../../theme";
import { type PickupLocationResponse } from "@/logic/api";

interface PickupLocationListItemProps {
    location: PickupLocationResponse;
    onEdit: (location: PickupLocationResponse) => void;
    onDelete: (location: PickupLocationResponse) => void;
    disabled?: boolean;
}

export const PickupLocationListItem: React.FC<PickupLocationListItemProps> = ({
    location,
    onEdit,
    onDelete,
    disabled = false
}) => {
    const { t } = useTranslation();

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                bgcolor: "white",
                transition: "all 0.2s ease",
                "&:hover": {
                    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)"
                },
            }}
            data-testid={`pickup-location-item-${location.uuid}`}
        >
            {/* Active / inactive status dot */}
            <Tooltip title={location.isActive
                ? t("settings.shuttlePickup.pickupLocations.status.active")
                : t("settings.shuttlePickup.pickupLocations.status.inactive")}
            >
                <Box
                    sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor: location.isActive ? "#22c55e" : "#9ca3af",
                        flexShrink: 0,
                        mr: 2,
                        cursor: "default"
                    }}
                    data-testid={`pickup-location-status-${location.uuid}`}
                />
            </Tooltip>

            {/* Name */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    variant="body1"
                    sx={{
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                    }}
                    data-testid={`pickup-location-name-${location.uuid}`}
                >
                    {location.name}
                </Typography>
            </Box>

            {/* Edit / Delete actions */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Tooltip title={t("settings.shuttlePickup.pickupLocations.editLocation")}>
                    <span>
                        <IconButton
                            onClick={() => onEdit(location)}
                            disabled={disabled}
                            size="small"
                            sx={{
                                color: "#6b7280",
                                "&:hover": {
                                    color: theme.palette.primary.main,
                                    bgcolor: "rgba(63, 81, 181, 0.1)"
                                }
                            }}
                            data-testid={`edit-pickup-location-${location.uuid}`}
                        >
                            <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </span>
                </Tooltip>

                <Tooltip title={t("settings.shuttlePickup.pickupLocations.deleteDialog.title")}>
                    <span>
                        <IconButton
                            onClick={() => onDelete(location)}
                            disabled={disabled}
                            size="small"
                            sx={{
                                color: "#6b7280",
                                "&:hover": {
                                    color: "#ef4444",
                                    bgcolor: "rgba(239, 68, 68, 0.1)"
                                },
                                "&:disabled": { color: "#ccc" }
                            }}
                            data-testid={`delete-pickup-location-${location.uuid}`}
                        >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
};
